import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { Axios } from "api/axios";

/**
 * Fetch challan list for a specific sales order service
 */
export const fetchChallanList = createAsyncThunk(
  "challan/fetchList",
  async (salesOrderServiceId: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.LIST(salesOrderServiceId)
      );
      return response.data?.data || response.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create a new challan
 */
export const createChallan = createAsyncThunk(
  "challan/create",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await Axios.post(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.CREATE,
        payload
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch a single challan by ID for editing.
 * Returns { challan, items } where items are pre-filtered:
 *   - Items in this challan (in_this_challan = true)
 *   - Free items (challan_status = false)
 */
export const fetchChallanById = createAsyncThunk(
  "challan/fetchById",
  async (challanId: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.GET_BY_ID(challanId)
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update an existing challan
 */
export const updateChallan = createAsyncThunk(
  "challan/update",
  async ({ challanId, payload }: { challanId: string; payload: any }, { rejectWithValue }) => {
    try {
      const response = await Axios.put(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.UPDATE(challanId),
        payload
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch initialization data for a NEW challan.
 */
export const fetchChallanCreateData = createAsyncThunk(
  "challan/fetchCreateData",
  async (salesOrderServiceId: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.GET_CREATE_DATA(salesOrderServiceId)
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Download Challan PDF
 */
export const downloadChallanPdf = createAsyncThunk(
  "challan/downloadPdf",
  async ({ id, challanNo }: { id: string; challanNo: string }, { rejectWithValue }) => {
    try {
      const response = await Axios.get(API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.DOWNLOAD_PDF(id), {
        responseType: "blob", // Important for downloading files
      });

      // Create a Blob from the PDF stream
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link element to trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Service_Challan_${challanNo}.pdf`); // Set the file name
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);

      return true;
    } catch (error: any) {
      if (error.response && error.response.data instanceof Blob) {
        // Log the error message generated by the backend API if returned via Blob
        const textPromise = error.response.data.text();
        textPromise.then((text: any) => console.error("Error from backend API:", text));
      }
      return rejectWithValue(error.response?.data || "Failed to download Challan PDF");
    }
  }
);

export const fetchAccessoryReturnData = createAsyncThunk(
  "challan/fetchAccessoryReturnData",
  async (challanId: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.ACCESSORY_RETURNS(challanId)
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || error.message
      );
    }
  }
);

export const fetchAccessoryReturnEditData = createAsyncThunk(
  "challan/fetchAccessoryReturnEditData",
  async (
    { challanId, returnId }: { challanId: string; returnId: string },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.get(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.ACCESSORY_RETURNS_EDIT(challanId, returnId)
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || error.message
      );
    }
  }
);

export const updateAccessoryReturn = createAsyncThunk(
  "challan/updateAccessoryReturn",
  async (
    { challanId, returnId, payload }: { challanId: string; returnId: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.put(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.ACCESSORY_RETURNS_UPDATE(challanId, returnId),
        payload
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || error.message
      );
    }
  }
);

export const createAccessoryReturn = createAsyncThunk(
  "challan/createAccessoryReturn",
  async (
    { challanId, payload }: { challanId: string; payload: any },
    { rejectWithValue }
  ) => {
    try {
      const response: any = await Axios.post(
        API_ENDPOINT.SALES_ORDER.SERVICE.CHALLAN.ACCESSORY_RETURNS(challanId),
        payload
      );
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data || error.message
      );
    }
  }
);
